import { useEffect, useState } from "react";
import { X, Download, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const WhatsAppIcon = ({ className = "w-4 h-4" }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.198-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347zM12.057 21.785h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.002-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884zM20.52 3.449C18.24 1.245 15.24 0 12.045 0 5.463 0 .104 5.334.101 11.892c0 2.096.549 4.142 1.594 5.945L0 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.581 0 11.94-5.335 11.943-11.893a11.821 11.821 0 0 0-3.476-8.443z"/>
  </svg>
);

/**
 * Loads a server-rendered PNG report (weekly or monthly) and lets the user
 * Save / Share to WhatsApp. Same UX pattern as DailyDiary.
 */
export default function ReportPreview({ kind, onClose }) {
  const [src, setSrc] = useState(null);
  const [blob, setBlob] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let url = null;
    let cancelled = false;
    api.get(`/reports/${kind}`, { responseType: "blob" })
      .then((r) => {
        if (cancelled) return;
        url = URL.createObjectURL(r.data);
        setSrc(url);
        setBlob(r.data);
      })
      .catch(() => toast.error("Couldn't load report"));
    return () => { cancelled = true; if (url) URL.revokeObjectURL(url); };
  }, [kind]);

  const download = () => {
    if (!src) return;
    const link = document.createElement("a");
    link.href = src;
    link.download = `kobolog-${kind}-${new Date().toISOString().slice(0, 10)}.png`;
    link.click();
    toast.success("Saved");
  };

  const shareWhatsApp = async () => {
    if (!blob) return;
    setBusy(true);
    try {
      const file = new File([blob], `kobolog-${kind}.png`, { type: "image/png" });
      const text = `My Kobolog AI ${kind} report — every kobo, logged. kobolog.ai`;
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({ files: [file], title: "Kobolog Report", text });
          return;
        } catch (e) { console.debug("Share cancelled:", e?.name); }
      }
      // Desktop fallback: download + open wa.me
      download();
      window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center overflow-y-auto py-6" onClick={onClose}>
      <div className="relative max-w-sm w-full px-4" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute -top-1 -right-1 z-10 w-9 h-9 rounded-full bg-white/90 flex items-center justify-center shadow-lg" data-testid="close-report"><X className="w-4 h-4" /></button>

        <div className="rounded-2xl overflow-hidden shadow-2xl bg-kudi-green/40 aspect-[4/5]" data-testid={`report-${kind}-card`}>
          {!src ? (
            <div className="w-full h-full flex items-center justify-center">
              <Loader2 className="w-8 h-8 text-white animate-spin" />
            </div>
          ) : (
            <img src={src} alt={`Kobolog ${kind} report`} className="w-full h-full object-cover" />
          )}
        </div>

        <div className="mt-3 grid grid-cols-2 gap-2">
          <button onClick={download} disabled={!src || busy} className="bg-white text-kudi-green rounded-full py-3 font-semibold flex items-center justify-center gap-2 hover:bg-kudi-cream transition disabled:opacity-50" data-testid={`download-${kind}`}>
            <Download className="w-4 h-4" /> Save
          </button>
          <button onClick={shareWhatsApp} disabled={!blob || busy} className="bg-emerald-600 text-white rounded-full py-3 font-semibold flex items-center justify-center gap-2 hover:bg-emerald-700 transition disabled:opacity-50" data-testid={`share-${kind}`}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><WhatsAppIcon className="w-4 h-4" /> WhatsApp</>}
          </button>
        </div>
        <p className="text-center text-xs text-white/70 mt-3">Forward to family/business groups for instant social proof 📈</p>
      </div>
    </div>
  );
}
